<template>
  <el-form-item :label="label" :prop="propName">
    <div class="video-cover">
      <!-- 未设置封面：沿用 VImageUpload 的拖拽上传区 -->
      <el-upload
        v-show="!src"
        drag
        :http-request="choiceOver"
        action=""
        :show-file-list="false"
        accept="image/*"
      >
        <i class="el-icon-upload"/>
        <div class="el-upload__text">将{{ label }}拖动至此处，或 <em>点击上传</em></div>
        <div class="el-upload__tip" slot="tip">
          大小不可超过 <span class="primary-color">{{ humanizeMaxSize }}</span>，建议图片宽高比例为 <span class="primary-color">16:9</span>
        </div>
      </el-upload>

      <!-- 已设置封面：只读 URL + 预览图（尺寸、叉子、进度圈全部照抄 VVideoUpload） -->
      <el-input v-show="src" v-model="src" :readonly="true"/>

      <div v-show="src" class="preview-box">
        <div class="video-preview-box">
          <img
            class="img"
            :class="{ 'img-drag': is_drag }"
            :src="src"
            @click="choice"
            @drop.prevent="drop"
            @dragover.prevent
            @dragenter.prevent="is_drag = true"
            @dragleave="is_drag = false"
            alt=""
          />
          <el-progress v-show="isUploading" type="circle" :percentage="progress" class="img-put"/>
          <div v-show="!isUploading" class="icon-close-box pointer" @click="removeCover">
            <v-icon name="close"/>
          </div>
        </div>
      </div>

      <div v-if="src && autoPicked" class="cover-source-tip">
        <el-tag type="success" size="mini">系统自动截取</el-tag>
      </div>

      <div v-show="src && !isUploading" class="el-upload__tip">
        将新的{{ label }}拖动至上方，或
        <input ref="manualInput" @change="onManualChange" type="file" accept="image/*" style="display: none">
        <span class="primary-color pointer" @click="choice">点击替换</span>
      </div>

      <!-- 截帧中 -->
      <div v-if="isCapturing" class="capture-status">
        <i class="el-icon-loading"/> 正在从视频中自动截取画面…
      </div>

      <!-- 截帧结果：内嵌缩略图，点选即设为封面 -->
      <div v-else-if="frames.length" class="frames-box">
        <div class="frames-title">
          已截取 {{ frames.length }} 张画面，点击即可设为封面
          <span v-if="frames.length > 4" class="link" @click="isFramesDialogVisible = true">查看更多截图</span>
        </div>
        <div class="frames-grid">
          <div
            v-for="(frame, index) in frames"
            :key="index"
            class="frame-item"
            :class="{ active: index === selectedIndex, recommend: index === recommendedIndex }"
            @click="selectFrame(index)"
          >
            <img :src="frame.preview" alt="视频截图"/>
            <i v-if="index === selectedIndex" class="el-icon-check frame-check"/>
            <span v-else-if="index === recommendedIndex" class="frame-recommend">推荐</span>
          </div>
        </div>
      </div>

      <!-- 截帧失败 -->
      <div v-else-if="failedMessage" class="capture-failed">{{ failedMessage }}</div>

      <!-- 无视频且无封面时的说明 -->
      <div v-else-if="!src" class="cover-empty">
        {{ file ? '正在准备截取画面…' : '上传视频后，系统会自动截取画面并设为封面，无需手动准备封面图。' }}
      </div>
    </div>

    <!-- 全部截图 -->
    <el-dialog
      :visible.sync="isFramesDialogVisible"
      title="选择视频封面"
      width="760px"
      append-to-body
    >
      <div class="frames-dialog-grid">
        <div
          v-for="(frame, index) in frames"
          :key="index"
          class="frame-item large"
          :class="{ active: index === selectedIndex, recommend: index === recommendedIndex }"
          @click="selectFrame(index)"
        >
          <img :src="frame.preview" alt="视频截图"/>
          <i v-if="index === selectedIndex" class="el-icon-check frame-check"/>
          <span v-else-if="index === recommendedIndex" class="frame-recommend">推荐</span>
          <span class="frame-time">{{ formatTime(frame.time) }}</span>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="isFramesDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </el-form-item>
</template>

<script>
import { putImage, imageCdnHost } from '@/api/oss';
import { decimalPlaces, dividedBy } from '@/utils/number';

// 取样区间：跳过片头片尾
const FRAME_START = 0.05;
const FRAME_END = 0.95;
// 取样张数按时长自适应：约每 2 秒取一帧，限制在 4~8 张之间。
// 参考主流平台（B 站约 3~5 张、YouTube 约 3~4 张）：候选并非越多越好，数量一多反而挑花眼，
// 且每张都要 seek 一次、串行等待；预告片本身只有几十秒，2 秒间隔取 8 张已覆盖全程。
// 最终展示几张仍由「空白/重复过滤」决定，不硬凑数量
const FRAME_INTERVAL_SECONDS = 2;
const MIN_FRAME_COUNT = 4;
const MAX_FRAME_COUNT = 8;
// 与上一张保留画面的平均灰度差小于该值，视为同一画面，直接跳过（短视频常见）
const SIMILAR_GRAY_DIFF = 4;
// 输出尺寸（16:9）：详情页顶部素材区是固定 16:9（app.wxss 的 .video-box 100vw × 56.18vw），
// 封面必须同比例；分享海报用 canvas 5:4 铺底时只裁两侧，纵向内容仍完整
const TARGET_WIDTH = 1280;
const TARGET_HEIGHT = 720;
// 竖版视频上下裁 16:9 时从顶部附近取：人脸/主体多在上半部，居中会切在腰部只剩身体
const PORTRAIT_TOP_BIAS = 0.05;
// 采集阶段丢弃阈值（取严格值）：只丢弃可以确定是黑屏/白屏/纯色空白的画面，
// 避免把「整体偏暗但确实有内容」的现场视频误杀成"一张可用截图都没有"
const FLAT_MIN_BRIGHTNESS = 12;
const FLAT_MAX_BRIGHTNESS = 243;
const FLAT_VARIANCE = 15;
// 挑选推荐帧的宽松区间：偏暗/偏亮的帧不选作默认封面，但仍保留在候选里由用户决定
const MIN_BRIGHTNESS = 25;
const MAX_BRIGHTNESS = 235;

export default {
  props: {
    value: {
      type: String,
      default: '',
    },
    // 本地选中的视频文件（由 VVideoUpload 抛出），用于提取画面
    // 注意：File 实例在 Vue 2 的 prop 类型校验里不等于 Object（toString 为 [object File]），
    // 因此不做类型约束，否则控制台会报 type check failed
    file: {
      type: null,
      default: null,
    },
    label: {
      type: String,
      default: '视频封面',
    },
    prefix: {
      type: String,
      default: 'video-cover',
    },
    propName: {
      type: String,
      default: 'cover',
    },
    maxSize: {
      type: Number,
      default: 10485760, // 10 MB
    },
  },
  data: () => ({
    src: '',
    objectKey: '',
    frames: [], // [{ preview, blob, time, variance }]
    recommendedIndex: -1, // 算法推荐的帧（只打标，不直接替换已有封面）
    selectedIndex: -1, // 当前已设为封面的帧
    isCapturing: false,
    isUploading: false,
    progress: 0,
    isFramesDialogVisible: false,
    is_drag: false, // 图片是否处于拖拽替换区上方
    failedMessage: '',
    autoPicked: false, // 封面是否由系统自动截取（用于提示标签）
    captureSeq: 0, // 截帧序号，避免切换视频后旧任务回写
  }),
  computed: {
    humanizeMaxSize() {
      return this.getHumanizedSize(this.maxSize);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(e) {
        if (e === this.src) return;

        this.src = e;
        this.objectKey = e ? e.replace(imageCdnHost, '') : '';
      },
    },
    src(e) {
      this.$emit('input', e);
    },
    file: {
      handler(file) {
        if (!file) {
          this.cancelCapture();
          return;
        }
        this.captureFrames(file);
      },
    },
  },
  beforeDestroy() {
    this.cancelCapture();
  },
  methods: {
    // 取消进行中的截帧并清空截图
    cancelCapture() {
      this.captureSeq += 1;
      this.resetFrames();
    },
    resetFrames() {
      this.frames.forEach(frame => frame.preview && URL.revokeObjectURL(frame.preview));
      this.frames = [];
      this.recommendedIndex = -1;
      this.selectedIndex = -1;
      this.failedMessage = '';
      this.isCapturing = false;
    },
    // 从本地视频文件提取画面
    async captureFrames(file) {
      const seq = ++this.captureSeq;
      this.resetFrames();

      if (!file.type || !file.type.startsWith('video/')) return;

      this.isCapturing = true;
      let capturedFrames = [];
      const videoUrl = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.src = videoUrl;

      try {
        await this.waitForEvent(video, 'loadedmetadata', 10000);
        if (seq !== this.captureSeq) return;

        const duration = video.duration;
        if (!duration || !isFinite(duration)) throw new Error('无法读取视频时长');

        const sourceWidth = video.videoWidth || TARGET_WIDTH;
        const sourceHeight = video.videoHeight || TARGET_HEIGHT;
        const canvas = document.createElement('canvas');
        canvas.width = TARGET_WIDTH;
        canvas.height = TARGET_HEIGHT;
        const context = canvas.getContext('2d');

        // 取样张数按时长自适应
        const sampleSpan = duration * (FRAME_END - FRAME_START);
        const frameCount = Math.min(
          MAX_FRAME_COUNT,
          Math.max(MIN_FRAME_COUNT, Math.round(sampleSpan / FRAME_INTERVAL_SECONDS)),
        );

        capturedFrames = [];
        let previousGray = null;
        for (let i = 0; i < frameCount; i++) {
          if (seq !== this.captureSeq) return;

          const time = FRAME_START * duration + sampleSpan * i / (frameCount - 1);
          await this.seekTo(video, time);
          if (seq !== this.captureSeq) return;

          this.drawFrame(context, video, sourceWidth, sourceHeight);

          // 先判断再生成图片：黑屏/白屏/纯色空白，以及与上一张几乎相同的画面，直接跳过
          const measure = this.measureFrame(canvas);
          if (this.isFlatFrame(measure)) continue;
          if (previousGray && this.isSimilarGray(previousGray, measure.gray)) continue;

          const blob = await this.canvasToBlob(canvas);
          if (!blob) continue;

          previousGray = measure.gray;
          capturedFrames.push({
            preview: URL.createObjectURL(blob),
            blob,
            time,
            sharpness: measure.sharpness,
            brightness: measure.brightness,
          });
        }

        if (seq !== this.captureSeq) return;

        if (!capturedFrames.length) {
          this.failedMessage = '未能截取到画面，请手动上传封面';
          return;
        }

        // 一张可用画面都没截到（全黑/全白/纯色，或画面全部重复）：不展示候选、也不自动设封面
        // （黑封面会把播放器海报和分享卡片一起弄脏，比没有封面更糟），改为提示用户手动上传
        if (!capturedFrames.length) {
          this.failedMessage = '未截取到可用画面（视频为纯黑/纯白/纯色，或画面重复），请手动上传封面';
          return;
        }

        this.frames = capturedFrames;
        this.recommendedIndex = this.pickRecommendedIndex(capturedFrames);

        // 只在当前没有封面时自动设置一张；已有封面（多为人工挑选）不强行替换，
        // 仅把截图与「推荐」标出来，等用户主动点选
        if (!this.src) {
          this.selectFrame(this.recommendedIndex, true);
        }
      } catch (e) {
        if (seq === this.captureSeq) {
          this.failedMessage = '自动截取画面失败（可能浏览器不支持该视频编码），请手动上传封面';
        }
      } finally {
        video.removeAttribute('src');
        URL.revokeObjectURL(videoUrl);
        // 截帧被新视频打断时，释放本次已生成但未采用的截图
        if (seq !== this.captureSeq) {
          capturedFrames.forEach(frame => frame.preview && URL.revokeObjectURL(frame.preview));
        } else {
          this.isCapturing = false;
        }
      }
    },
    // 等待媒体事件，带超时兜底
    waitForEvent(element, name, timeout) {
      return new Promise((resolve, reject) => {
        let settled = false;
        const cleanup = () => {
          element.removeEventListener(name, onSuccess);
          element.removeEventListener('error', onError);
          clearTimeout(timer);
        };
        const onSuccess = () => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve();
        };
        const onError = () => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error('load-error'));
        };
        const timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(new Error('timeout'));
        }, timeout);

        element.addEventListener(name, onSuccess);
        element.addEventListener('error', onError);
      });
    },
    // 定位到指定时间点，依赖 seeked 事件而不是固定延时
    seekTo(video, time) {
      return new Promise(resolve => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          video.removeEventListener('seeked', finish);
          clearTimeout(timer);
          resolve();
        };
        const timer = setTimeout(finish, 3000);

        video.addEventListener('seeked', finish);
        video.currentTime = time;
      });
    },
    // 裁成 16:9 后绘制到固定尺寸画布：
    //  - 横版：左右居中裁
    //  - 竖版：上下裁，且从顶部附近取，保证能截到人脸/上半身
    drawFrame(context, video, sourceWidth, sourceHeight) {
      const targetRatio = TARGET_WIDTH / TARGET_HEIGHT;
      const sourceRatio = sourceWidth / sourceHeight;
      let sw = sourceWidth;
      let sh = sourceHeight;
      let sx = 0;
      let sy = 0;
      if (sourceRatio > targetRatio) {
        sw = sourceHeight * targetRatio;
        sx = (sourceWidth - sw) / 2;
      } else {
        sh = sourceWidth / targetRatio;
        sy = (sourceHeight - sh) * PORTRAIT_TOP_BIAS;
      }
      context.drawImage(video, sx, sy, sw, sh, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);
    },
    // 缩到 64x36 取样，一次算出三个指标：
    //  - sharpness（拉普拉斯方差）：清晰度，用来避开运动模糊帧
    //  - brightness（平均灰度）：识别黑屏/白屏
    //  - variance（灰度方差）：识别纯色/渐变空白画面
    measureFrame(canvas) {
      const sampleWidth = 64;
      const sampleHeight = 36;
      const sample = document.createElement('canvas');
      sample.width = sampleWidth;
      sample.height = sampleHeight;
      const context = sample.getContext('2d');
      context.drawImage(canvas, 0, 0, sampleWidth, sampleHeight);
      const data = context.getImageData(0, 0, sampleWidth, sampleHeight).data;

      const sampleTotal = sampleWidth * sampleHeight;
      const gray = new Float64Array(sampleTotal);
      let brightnessSum = 0;
      let brightnessSquareSum = 0;
      for (let i = 0, p = 0; i < data.length; i += 4, p++) {
        const value = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        gray[p] = value;
        brightnessSum += value;
        brightnessSquareSum += value * value;
      }
      const brightnessMean = brightnessSum / sampleTotal;

      let laplacianSum = 0;
      let laplacianSquareSum = 0;
      let count = 0;
      for (let y = 1; y < sampleHeight - 1; y++) {
        for (let x = 1; x < sampleWidth - 1; x++) {
          const p = y * sampleWidth + x;
          const laplacian = 4 * gray[p] - gray[p - 1] - gray[p + 1] - gray[p - sampleWidth] - gray[p + sampleWidth];
          laplacianSum += laplacian;
          laplacianSquareSum += laplacian * laplacian;
          count += 1;
        }
      }
      const laplacianMean = laplacianSum / count;

      return {
        sharpness: laplacianSquareSum / count - laplacianMean * laplacianMean,
        brightness: brightnessMean,
        variance: brightnessSquareSum / sampleTotal - brightnessMean * brightnessMean,
        gray, // 供相邻帧查重使用
      };
    },
    // 两帧灰度几乎一致（平均差很小）视为同一画面
    isSimilarGray(previousGray, gray) {
      let diffSum = 0;
      for (let i = 0; i < gray.length; i++) diffSum += Math.abs(gray[i] - previousGray[i]);
      return diffSum / gray.length < SIMILAR_GRAY_DIFF;
    },
    // 黑屏/白屏/纯色空白画面：亮度越界，或整体灰度方差极低
    isFlatFrame(frame) {
      return frame.brightness < FLAT_MIN_BRIGHTNESS
        || frame.brightness > FLAT_MAX_BRIGHTNESS
        || frame.variance < FLAT_VARIANCE;
    },
    // 推荐帧：亮度正常的前提下挑最清晰的一张，并轻微偏向中段，避开片头标题卡与片尾黑场
    pickRecommendedIndex(frames) {
      const center = (frames.length - 1) / 2;
      let best = -1;
      let bestScore = -1;
      for (let i = 0; i < frames.length; i++) {
        const frame = frames[i];
        if (frame.brightness < MIN_BRIGHTNESS || frame.brightness > MAX_BRIGHTNESS) continue;

        const score = frame.sharpness * (1 - Math.abs(i - center) / frames.length * 0.6);
        if (score > bestScore) {
          best = i;
          bestScore = score;
        }
      }
      return best >= 0 ? best : Math.floor(frames.length / 2);
    },
    canvasToBlob(canvas) {
      return new Promise(resolve => {
        if (canvas.toBlob) {
          canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.85);
          return;
        }
        const base64 = canvas.toDataURL('image/jpeg', 0.85).split(',')[1];
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        resolve(new Blob([bytes], { type: 'image/jpeg' }));
      });
    },
    // 点选某张截图 → 设为封面。已经传过的帧直接复用地址，来回切换不再重复上传
    selectFrame(index, isAuto = false) {
      const frame = this.frames[index];
      if (!frame || this.isUploading) return;

      this.selectedIndex = index;
      this.isFramesDialogVisible = false;
      this.autoPicked = isAuto;

      if (frame.url) {
        this.objectKey = frame.objectKey;
        this.src = frame.url;
        return;
      }

      this.uploadBlob(frame.blob, frame);
    },
    choice() {
      this.$refs.manualInput.value = '';
      this.$refs.manualInput.click();
    },
    choiceOver(file) {
      this.handleManualImage(file.file);
    },
    drop(e) {
      this.is_drag = false;
      this.handleManualImage(e.dataTransfer.files[0]);
    },
    onManualChange(e) {
      this.handleManualImage(e.target.files[0]);
    },
    // 手动上传：校验后直接走图片直传 OSS（与 VImageUpload 同一套校验）
    handleManualImage(imageData) {
      if (!imageData) return;
      if (!imageData.type || !imageData.type.startsWith('image/')) {
        return this.$message.error('请选择图片类型的文件');
      }
      if (imageData.size > this.maxSize) {
        return this.$message.error(`文件大小不可超过 ${this.humanizeMaxSize}`);
      }

      this.selectedIndex = -1;
      this.autoPicked = false;
      this.uploadBlob(imageData);
    },
    // 统一走已有的图片直传 OSS 通道；来自截图时把上传结果记在该帧上，下次选中直接复用
    uploadBlob(imageData, frame = null) {
      this.isUploading = true;
      this.progress = 10;
      putImage({
        bucket_prefix: this.prefix,
        imageData,
        onProgress: this.handleProgress,
      }).then(objectKey => {
        this.isUploading = false;
        this.progress = 0;
        if (!objectKey) {
          this.$message.error('封面上传失败，请重新选择或手动上传');
          return;
        }

        const url = `${imageCdnHost}${objectKey}`;
        this.objectKey = objectKey;
        this.src = url;
        if (frame) {
          this.$set(frame, 'url', url);
          this.$set(frame, 'objectKey', objectKey);
        }
      });
    },
    removeCover() {
      this.src = '';
      this.objectKey = '';
      this.selectedIndex = -1;
      this.autoPicked = false;
    },
    handleProgress(e) {
      if (e.total) this.progress = Math.floor(e.loaded / e.total * 100);
    },
    formatTime(seconds) {
      const total = Math.max(0, Math.floor(seconds || 0));
      const minute = Math.floor(total / 60);
      const second = total % 60;
      return `${minute}:${second < 10 ? '0' : ''}${second}`;
    },
    getHumanizedSize(number) {
      if (number < 1024) return `${number} B`;
      if (number < 1048576) return `${decimalPlaces(dividedBy(number, 1024))} KB`;
      if (number < 1073741824) return `${decimalPlaces(dividedBy(number, 1048576))} MB`;
      return `${decimalPlaces(dividedBy(number, 1073741824))} GB`;
    },
  },
};
</script>

<style src="./index.css" scoped/>
